import { GoogleAnalyticsEventType } from 'enums/GoogleAnalyticsEventType';
import {
  BankAccountDataVariables,
  CuroApplicationVariables,
  OrigGpsBankAccountInformationVariables,
} from 'enums/curo/Variables';
import { getApplicationSelector } from 'handlers/curo-handlers/selectors';
import { useAppSelector } from 'hooks/reduxHooks';
import { useCurrentStep } from 'hooks/useCurrentStep';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import useGoogleAnalytics from 'hooks/useGoogleAnalytics';
import { OnlineOfferFlowSteps } from 'pages/CuroPages/ApplicationProcess/steps/GenerateLoanOffersStep/constants';
import { ApplicationIsNotApprovedNOAA } from 'pages/CuroPages/EndPages';
import { NotFoundPageContent } from 'pages/CuroPages/NotFoundPage/NotFoundPage';
import { TableValue } from 'product_modules/api/Types';
import {
  runApplicationSummitLead,
  runSecondChanceCashflowAndAutoOrigination,
  saveBankInformation,
  updateCurrentApplication,
  updatePageDisplayedTechnical,
} from 'thunks';

/* eslint-disable no-restricted-imports */
import { BranchOfferType, BankAccountType } from '../constants';
import {
  BranchEndStep,
  SelectLoanOfferStep,
  BankAccountExistingOrOtherStep,
  BankAccountSelectStep,
  SetupAutoPayFillingOptionsStep,
  SetupAutoPayStep,
  BankInformationPlaidIntegrationWaitingStep,
  BankInformationPlaidStep,
  ConnectBankAccountForFundingStep,
} from '../steps';

const OnlineOfferFlow = () => {
  const application = useAppSelector(getApplicationSelector)!;
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const trackEvent = useGoogleAnalytics();

  const isPlaidIntegrationsCompleted =
    application.variables[CuroApplicationVariables.PlaidAuthAndIdentityIntegrationsPassed] &&
    application.variables[CuroApplicationVariables.PlaidCraIntegrationsPassed];
  const smallLoanRefiJourney = application.variables[CuroApplicationVariables.SmallLoanRefiJourney];
  const bankAccountData = application.variables[
    CuroApplicationVariables.BankAccountData
  ] as TableValue;
  const branchType = application.variables[CuroApplicationVariables.BranchType] as BranchOfferType;

  const getInitialStep = () => {
    if (application.variables[CuroApplicationVariables.PpcJourney]) {
      dispatchWithUnwrap(
        updateCurrentApplication({
          variables: {
            [CuroApplicationVariables.PpcJourneyOfferDisplayedTechnical]: true,
          },
        }),
      );

      return OnlineOfferFlowSteps.SelectPPCJourneyLoanOffer;
    }

    if (!!smallLoanRefiJourney) {
      return OnlineOfferFlowSteps.SelectSLRefiLoanOffer;
    } else {
      return OnlineOfferFlowSteps.SelectOnlineLoanOffer;
    }
  };

  const [currentStep, setCurrentStep] = useCurrentStep<OnlineOfferFlowSteps>(getInitialStep());

  const getIsSeveralBankAccounts = (bankData: TableValue) => {
    return (
      bankData?.filter(
        (account) => account[BankAccountDataVariables.BankAccountType] === 'checking',
      )?.length > 1
    );
  };

  const handleFinishAndGoToEndPage = () => {
    dispatchWithUnwrap(
      updateCurrentApplication({
        variables: {
          [CuroApplicationVariables.SendNextStepsEmail]: true,
        },
      }),
    );
    dispatchWithUnwrap(runApplicationSummitLead({ applicationId: application.id }));

    const nextStep =
      branchType === BranchOfferType.Central
        ? OnlineOfferFlowSteps.CentralBranchOfferStep
        : OnlineOfferFlowSteps.PhysicalBranchOfferStep;

    setCurrentStep(nextStep);
  };

  const handlePlaidCompleted = async () => {
    let currentApplication = application;

    if (
      !currentApplication.variables[
        CuroApplicationVariables.SecondChanceCashflowAndAutoOriginationStrategyRan
      ]
    ) {
      currentApplication = await dispatchWithUnwrap(
        runSecondChanceCashflowAndAutoOrigination({ applicationId: application?.id }),
      );
      trackEvent(GoogleAnalyticsEventType.OnlineOrigination);
    }

    if (currentApplication.variables[CuroApplicationVariables.AorPass] === false) {
      dispatchWithUnwrap(
        updateCurrentApplication({
          variables: {
            [CuroApplicationVariables.SendOfferDetailsEmail]: true,
          },
        }),
      );
      handleFinishAndGoToEndPage();
    } else {
      const isSeveralAccounts = getIsSeveralBankAccounts(
        currentApplication.variables[CuroApplicationVariables.BankAccountData] as TableValue,
      );
      const nextStep = isSeveralAccounts
        ? OnlineOfferFlowSteps.BankAccountSelectStep
        : OnlineOfferFlowSteps.SetupAutoPayFillingOptionsStep;

      setCurrentStep(nextStep);
    }
  };

  const handleIsPlaidDataCollected = () => {
    if (isPlaidIntegrationsCompleted) {
      handlePlaidCompleted();
    } else {
      dispatchWithUnwrap(
        updatePageDisplayedTechnical({
          applicationId: application?.id,
          pageVariableToUpdate:
            CuroApplicationVariables.ConnectBankAccountForFundingDisplayedTechnical,
        }),
      );
      setCurrentStep(OnlineOfferFlowSteps.ConnectBankAccountForFundingStep);
    }
  };

  const handleSLRefiLoanOffer = () => {
    if (!!application.variables[CuroApplicationVariables.GpsBankAccountVerified]) {
      setCurrentStep(OnlineOfferFlowSteps.BankAccountExistingOrOtherAccount);
    } else {
      handleIsPlaidDataCollected();
    }
  };

  const handleAcceptLoanOffer = () => {
    if (smallLoanRefiJourney !== true) {
      handleIsPlaidDataCollected();
    } else {
      handleSLRefiLoanOffer();
    }
  };

  const selectExistingOrOtherAccount = async (selectedAccount: BankAccountType) => {
    if (selectedAccount === BankAccountType.Existing) {
      const origGpsBankAccountInformation = application.variables[
        CuroApplicationVariables.OrigGpsBankAccountInformation
      ] as TableValue;

      await dispatchWithUnwrap(
        saveBankInformation({
          applicationId: application.id,
          bankInformation: {
            bankAccountType: origGpsBankAccountInformation[0][
              OrigGpsBankAccountInformationVariables.AccountType
            ] as string,
            accountNumber: origGpsBankAccountInformation[0][
              OrigGpsBankAccountInformationVariables.BankAccountNumber
            ] as string,
            routingNumber: origGpsBankAccountInformation[0][
              OrigGpsBankAccountInformationVariables.BankRoutingNumber
            ] as string,
          },
        }),
      );
      setCurrentStep(OnlineOfferFlowSteps.SetupAutoPayFillingOptionsStep);
    } else {
      handleIsPlaidDataCollected();
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case OnlineOfferFlowSteps.SelectSLRefiLoanOffer:
        return <SelectLoanOfferStep variant='refi' onStepComplete={handleAcceptLoanOffer} />;
      case OnlineOfferFlowSteps.SelectOnlineLoanOffer:
        return <SelectLoanOfferStep variant='online' onStepComplete={handleAcceptLoanOffer} />;
      case OnlineOfferFlowSteps.SelectPPCJourneyLoanOffer:
        return (
          <SelectLoanOfferStep
            variant="ppc"
            onStepComplete={handleAcceptLoanOffer}
            onCancel={() => setCurrentStep(OnlineOfferFlowSteps.ApplicationIsNotApprovedNOAAStep)}
          />
        );
      case OnlineOfferFlowSteps.ConnectBankAccountForFundingStep:
        return (
          <ConnectBankAccountForFundingStep
            onStepComplete={handleFinishAndGoToEndPage}
            onPlaid={() => setCurrentStep(OnlineOfferFlowSteps.BankInformationPlaid)}
          />
        );
      case OnlineOfferFlowSteps.BankAccountExistingOrOtherAccount:
        return <BankAccountExistingOrOtherStep onStepComplete={selectExistingOrOtherAccount} />;
      case OnlineOfferFlowSteps.BankAccountSelectStep:
        return (
          <BankAccountSelectStep
            onContinueClick={() =>
              setCurrentStep(OnlineOfferFlowSteps.SetupAutoPayFillingOptionsStep)
            }
          />
        );
      case OnlineOfferFlowSteps.SetupAutoPayFillingOptionsStep:
        return (
          <SetupAutoPayFillingOptionsStep
            withBackButton={getIsSeveralBankAccounts(bankAccountData)}
            onContinueClick={() => setCurrentStep(OnlineOfferFlowSteps.SetupAutoPayStep)}
            onBackButtonClick={() => setCurrentStep(OnlineOfferFlowSteps.BankAccountSelectStep)}
          />
        );
      case OnlineOfferFlowSteps.SetupAutoPayStep:
        return (
          <SetupAutoPayStep
            onBackButtonClick={() =>
              setCurrentStep(OnlineOfferFlowSteps.SetupAutoPayFillingOptionsStep)
            }
          />
        );
      case OnlineOfferFlowSteps.BankInformationPlaid:
        return (
          <BankInformationPlaidStep
            onStepExit={() => setCurrentStep(OnlineOfferFlowSteps.ConnectBankAccountForFundingStep)}
            onStepFailed={() =>
              setCurrentStep(OnlineOfferFlowSteps.BankInformationPLaidIntegrationFailed)
            }
            onStepComplete={() =>
              setCurrentStep(OnlineOfferFlowSteps.BankInformationPlaidIntegrationWaiting)
            }
          />
        );
      case OnlineOfferFlowSteps.BankInformationPlaidIntegrationWaiting:
        return (
          <BankInformationPlaidIntegrationWaitingStep
            onStepFail={handleFinishAndGoToEndPage}
            onStepComplete={handlePlaidCompleted}
          />
        );
      case OnlineOfferFlowSteps.PhysicalBranchOfferStep:
        return <BranchEndStep branchType={BranchOfferType.Physical} isPhysicalOffer />;
      case OnlineOfferFlowSteps.CentralBranchOfferStep:
        return <BranchEndStep branchType={BranchOfferType.Central} />;

      case OnlineOfferFlowSteps.BankInformationPLaidIntegrationFailed:
        return <NotFoundPageContent />;

      case OnlineOfferFlowSteps.ApplicationIsNotApprovedNOAAStep:
        return <ApplicationIsNotApprovedNOAA />;

      default:
        return null as never;
    }
  };

  return renderCurrentStep();
};

export default OnlineOfferFlow;
